import { t } from 'i18next';
import React, { FC, FormEvent, FormEventHandler, useMemo } from 'react';
import { TextInput, Icon, Button } from 'shamrock-clover-ui';
import type { MFAError } from './Login';
import { MFAErrorSection } from './mfaLogin/MFAErrorSection';
import styles from './styles/MFA-login.scss';
import { V2LoginService } from '../../services/login.v2.service';
import { Variant } from 'shamrock-clover-ui/dist/clover/components/Snackbar/Snackbar';
import { isErrorObject } from './utils';

interface LoginFormProps {
  handleLogin: () => void;
  email: string;
  isLoading: boolean;
  handleEmailChange: React.ChangeEventHandler<HTMLInputElement>;
  showPassword: boolean;
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
  password: string;
  setPassword: React.Dispatch<React.SetStateAction<string>>;
  verificationCode: string;
  setVerificationCode: React.Dispatch<React.SetStateAction<string>>;
  navigateForgotPassword: React.MouseEventHandler<HTMLButtonElement>;
  setShowSnackbar: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  setSnackbarDuration: React.Dispatch<React.SetStateAction<number>>;
  setSnackbarVariant: React.Dispatch<React.SetStateAction<Variant>>;
  mfaError: MFAError;
}

export const MFALogin: FC<LoginFormProps> = ({
  handleLogin,
  email,
  isLoading,
  handleEmailChange,
  showPassword,
  setShowPassword,
  password,
  setPassword,
  navigateForgotPassword,
  verificationCode,
  setVerificationCode,
  setShowSnackbar,
  setSnackbarMessage,
  setSnackbarDuration,
  setSnackbarVariant,
  mfaError,
}) => {
  const loginService = useMemo(() => new V2LoginService(), []);

  const handleFormSubmit: FormEventHandler = (e) => {
    e.preventDefault();

    if (password.length >= 8) {
      handleLogin();
    }
  };

  const resendVerificationCode = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const loweredEmail = email.toLowerCase();
    const result = await loginService.sendVerificationCode(loweredEmail);
    if (!isErrorObject(result)) {
      setSnackbarMessage('A new verification code has been sent successfully.');
      setSnackbarVariant('success');
    } else {
      setSnackbarMessage(result.error);
      setSnackbarVariant('error');
    }
    setShowSnackbar(true);
    setSnackbarDuration(10000);
  };

  const codeOnChange = (value: string) => {
    const input = Array.from(value.matchAll(/^\d+/g)).join();
    setVerificationCode(input);
  };

  return (
    <form className={styles.MFALoginForm} onSubmit={handleFormSubmit}>
      <div className={styles.loginHeader}>
        <h2 data-testid="login-header">{t('login')}</h2>
        <p>
          <strong>We sent a verification code to your email.</strong>
          <br />
          <span> Enter your password and verification code below to log in.</span>
        </p>
      </div>
      <div data-testid="email-field" className={styles.loginEmailContainer}>
        <TextInput
          id="email"
          label={t('email')}
          variant="filled"
          inputType="text"
          value={email}
          disabled={true}
          autoComplete={false}
          onChange={handleEmailChange}
        />
      </div>
      <div data-testid="password-field" className={styles.loginPasswordContainer}>
        <TextInput
          id="password"
          label={t('password')}
          className={styles.loginPasswordText}
          variant="filled"
          inputType={showPassword ? 'text' : 'password'}
          value={password}
          disabled={isLoading}
          autoComplete={false}
          onChange={({ target }) => setPassword(target.value)}
        />
        <Icon
          data-testid="password-reveal"
          className={styles.loginPasswordIcon}
          onClick={() => setShowPassword(!showPassword)}
          icon={showPassword ? 'eyeShow' : 'eyeHide'}
        />
      </div>
      <div data-testid="verification-field" className={styles.verificationCodeContainer}>
        <TextInput
          id="verify"
          label="Verification Code"
          className={styles.loginPasswordText}
          variant="filled"
          value={verificationCode}
          disabled={isLoading}
          autoComplete={false}
          onChange={({ target: { value } }) => codeOnChange(value)}
          maxLength={4}
        />
        <p>
          Didn&apos;t receive a code?
          <button onClick={resendVerificationCode} type="button">
            Resend verification code
          </button>
        </p>
      </div>
      {mfaError.key.length > 0 && (
        <MFAErrorSection mfaError={mfaError} navigateForgotPassword={navigateForgotPassword} />
      )}
      <div className={styles.buttonContainer}>
        <Button
          buttonStyle="solid"
          disabled={password.length < 8 || verificationCode.length < 4 || isLoading ||
            mfaError.key === 'account_locked'}
          width="100%"
          height="40px"
          data-testid="login-button"
          type="submit"
        >
          <span className={styles.buttonText}>{t('log_in')}</span>
        </Button>
        {mfaError.key !== 'account_locked' && (
          <button
            data-testid="password-reset"
            className={styles.forgotPassword}
            onClick={navigateForgotPassword}
            type="button"
          >
            {t('forgot_password')}
          </button>
        )}
      </div>
    </form>
  );
};
